import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "../../../../../redux";
import { Iconography, IconographySizes, Button } from "@hid-galaxy-ui/galaxy-react";
import "./ReportCategoryModal.scss";
import { getHidRolesSagaAction } from "../../../../../redux/saga/user/saga-actions";

/* ===================== TYPES ===================== */
export type CategoryFormData = {
  name: string;
  description: string;
  viewOnlyRoles: string[];
  editRoles: string[];
};

type Props = {
  mode: "create" | "edit";
  open: boolean;
  initialData?: CategoryFormData;
  readOnly?: boolean;
  onClose: () => void;
  onSubmit: (data: CategoryFormData) => void;
};

/* ===================== MULTI SELECT DROPDOWN ===================== */
const MultiSelectDropdown: React.FC<{
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((r) => r !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="gdl-multiselect" ref={containerRef}>
      <label>{label}</label>
      <div
        className="gdl-multiselect__input"
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected.length === 0 ? <span className="placeholder">Select</span> : <span>{selected.length} selected</span>}
        <Iconography icon="angleDown" size={IconographySizes.Small} />
      </div>

      {isOpen && (
        <ul className="gdl-multiselect__list">
          {options.map((option) => (
            <li
              key={option}
              className={selected.includes(option) ? "selected" : ""}
              onClick={() => toggleOption(option)}
            >
              {option}
              {selected.includes(option) && <Iconography icon="check" size={IconographySizes.Small} />}
            </li>
          ))}
        </ul>
      )}

      <div className="gdl-chip-group">
        {selected.map((role) => (
          <span key={role} className="gdl-chip">
            {role}
            <button type="button" onClick={() => onChange(selected.filter((r) => r !== role))}>
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ===================== CATEGORY MODAL ===================== */
const CategoryModal: React.FC<Props> = ({
  mode,
  open,
  initialData,
  readOnly = false,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [viewOnlyRoles, setViewOnlyRoles] = useState<string[]>([]);
  const [editRoles, setEditRoles] = useState<string[]>([]);

  const { hidRoles, status } = useAppSelector((state) => state.user);
  const roleOptions = useMemo(
    () => hidRoles.map((role) => role.name),
    [hidRoles]
  );
  const loadingRoles = status.hidRolesLoading;

  const dispatch = useAppDispatch();

  // Same role source as Add new user modal (GET /user/getRole, HID-assignable roles)
  useEffect(() => {
    if (open) {
      dispatch(getHidRolesSagaAction());
    }
  }, [dispatch, open]);

  // Initialize/reset form data whenever modal opens
  useEffect(() => {
    if (open) {
      setName(initialData?.name ?? "");
      setDescription(initialData?.description ?? "");
      setViewOnlyRoles(Array.isArray(initialData?.viewOnlyRoles) ? initialData.viewOnlyRoles : []);
      setEditRoles(Array.isArray(initialData?.editRoles) ? initialData.editRoles : []);
    }
  }, [initialData, open]);


  if (!open) return null;

  const title = mode === "create" ? "Create Category" : "Category Settings";
  const subtitle = mode === "create" ? "Configure a new Category type" : "Update configuration";

  return (
    <div className="gdl-category-modal gdl-modal-overlay">
      <div className="gdl-modal" role="dialog" aria-modal="true">
        <header className="gdl-modal__header">
          <h2>
            {title}
            <span className="modal-subtitle">{subtitle}</span>
          </h2>
          <button onClick={onClose} aria-label="Close">
            <Iconography icon="xmark" size={IconographySizes.Small} />
          </button>
        </header>

        <fieldset className="gdl-modal__body" disabled={readOnly}>
          <label>
            Category Name
            <input
              type="text"
              placeholder="Enter Category Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label>
            Description
            <span className="char-count">{description.length}/500</span>
            <textarea
              maxLength={500}
              placeholder="Enter description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <div className="gdl-access">
            <h4>Set Access For The Category</h4>
            {loadingRoles && roleOptions.length === 0 && <p>Loading roles...</p>}

            {!loadingRoles && (
              <>
                <MultiSelectDropdown
                  label="View Only"
                  options={roleOptions}
                  selected={viewOnlyRoles}
                  onChange={setViewOnlyRoles}
                />
                <MultiSelectDropdown
                  label="Edit"
                  options={roleOptions}
                  selected={editRoles}
                  onChange={setEditRoles}
                />
              </>
            )}
          </div>
        </fieldset>

        <footer className="gdl-modal__footer">
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button
            label={mode === "create" ? "Create Category" : "Save Changes"}
            variant="contained"
            color="primary"
            onClick={() => {
              if (readOnly) return;
              onSubmit({ name, description, viewOnlyRoles, editRoles });
            }}
            disabled={readOnly || !name.trim()}
          />
        </footer>
      </div>
    </div>
  );
};

export default CategoryModal;
